import { Box, Typography, MenuItem, CircularProgress } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import SidePanel from "../../components/SidePanel";
import TopBar from "../../components/TopBar";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button/Button";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import TextField from "@mui/material/TextField";
import "./style.css";
import PricingDeleteModal from "../../components/PricingDeleteModal";
import { getAllPricing } from "../../apis/pricing";
import { getAllPricings } from "../../actions/pricingAction";
import { getAllHelperPricings } from "../../actions/helperPricingAction";
import { getAllHelperPricing } from "../../apis/helperPricing";
import { getCountriesData } from "../../apis/master";

const HelperPricing = () => {
  const { indices } = useSelector((state: any) => state.helperPricingReducer);
  const dispatch = useDispatch();

  const [openDeletePricingModal, setOpenDeletePricingModal] = useState(false);
  const [pricingDetails, setPricingDetails] = useState(indices);
  const [filteredPricingDetails, setFilteredPricingDetails] = useState(indices);
  const [pricingData, setPricingData] = useState();
  const [country, setCountry] = useState(1);
  const [city, setCity] = useState("");
  const [countries, setCountries] = useState([]);
  const [countryData, setCountryData] = useState<any[]>([]);
  const [availableRegions, setAvailableRegions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const navigateToEdit = (id: number, packageSize: string) => {
    // Preserve country and city in URL when navigating to edit
    const countryParam = country ? `&country=${country}` : "";
    const cityParam = city ? `&city=${encodeURIComponent(city)}` : "";
    navigate(`/helper-pricing/edit/${id}?type=${packageSize}${countryParam}${cityParam}`);
  };

  // Function to fetch country data
  const fetchCountryData = async () => {
    try {
      const data = await getCountriesData();
      setCountryData(data);
    } catch (error) {
      console.error("Error fetching country data:", error);
    }
  };

  // Function to update available regions when country changes
  const updateAvailableRegions = (selectedCountryCode: string) => {
    if (!selectedCountryCode) {
      setAvailableRegions([]);
      return;
    }

    const countryInfo = countryData.find((c: any) => c.country_code === selectedCountryCode);
    if (countryInfo && countryInfo.regions) {
      setAvailableRegions(countryInfo.regions);
    } else {
      setAvailableRegions([]);
    }
  };

  // const handleDeletePricingModal = () => {
  //   setOpenDeletePricingModal(true);
  // };

  const onHandleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedCountryId = Number(event?.target?.value);
    setCountry(selectedCountryId);

    // Update URL params
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.set("country", selectedCountryId.toString());
    newSearchParams.delete("city"); // Reset city when country changes
    setSearchParams(newSearchParams);

    // Reset city selection when country changes
    setCity("");

    const region: any = countries.find((item: any) => item.id === selectedCountryId);
    if (region) {
      // Update available regions for the selected country
      updateAvailableRegions(region.value);
    }
    // Filtering will be handled by the useEffect that watches country and city changes
  };

  const onCityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedCity = event.target.value;
    setCity(selectedCity);

    // Update URL params
    const newSearchParams = new URLSearchParams(searchParams.toString());
    if (selectedCity) {
      newSearchParams.set("city", selectedCity);
    } else {
      newSearchParams.delete("city");
    }
    setSearchParams(newSearchParams);
    // Filtering will be handled by the useEffect that watches country and city changes
  };

  const getData = async () => {
    setLoading(true);
    const data = await getAllHelperPricing();
    dispatch(getAllHelperPricings(data));
    setPricingDetails(data);
    getCountries(data);
    setLoading(false);
  };

  const removeDuplicatesByValue = (items: any) => {
    const uniqueValuesMap: any = {};
    return items.filter((item: any) => {
      if (uniqueValuesMap[item.value]) {
        return false;
      }
      uniqueValuesMap[item.value] = true;
      return true;
    });
  };

  const getCountries = (pricingData: any) => {
    if (pricingData.length > 0) {
      let id = 1;
      let regionData = pricingData.map((detail: any) => ({
        id: id++,
        objId: detail._id,
        value: detail.country_code,
      }));

      regionData = removeDuplicatesByValue(regionData);
      setCountries(regionData);

      // Check if we have a country selected from URL params
      const countryParam = searchParams.get("country");
      let selectedCountryId = countryParam ? Number(countryParam) : country;
      
      // Set default to Ivory Coast if country is still at initial value and no URL param
      if (selectedCountryId === 1 && !countryParam) {
        const ivoryCoast = regionData.find((item: any) => item.value === "ivory-coast");
        if (ivoryCoast) {
          selectedCountryId = ivoryCoast.id;
          setCountry(ivoryCoast.id);
          // Update URL params to reflect default selection
          const newSearchParams = new URLSearchParams(searchParams.toString());
          newSearchParams.set("country", ivoryCoast.id.toString());
          setSearchParams(newSearchParams);
        }
      }

      // Filter by selected country
      const filtregion: any = regionData.find((item: any) => item.id === selectedCountryId);
      if (filtregion) {
        const filteredData = pricingData.filter(
          (item: any) => item.country_code === filtregion.value
        );

        if (filteredData && filteredData.length > 0) {
          setFilteredPricingDetails(filteredData);
        } else {
          // If no filtered data, still filter by country (empty array is better than all data)
          setFilteredPricingDetails([]);
        }
      } else {
        // If no country found, filter by Ivory Coast as default
        const ivoryCoastRegion = regionData.find((item: any) => item.value === "ivory-coast");
        if (ivoryCoastRegion) {
          const filteredData = pricingData.filter(
            (item: any) => item.country_code === ivoryCoastRegion.value
          );
          setFilteredPricingDetails(filteredData.length > 0 ? filteredData : []);
        } else {
          setFilteredPricingDetails([]);
        }
      }
    }
  };

  // const navigateToCreate = () => {
  //   navigate("/pricing/create");
  // };

  const currency = (region: string) => {
    const currencyData: Record<string, string> = {
      uk: "£",
      france: "€",
      "ivory-coast": "FCFA",
    };
    return currencyData[region];
  };

  useEffect(() => {
    //fetch all the pricing data
    getData();
    fetchCountryData();
  }, []);

  // Restore country and city from URL params on mount or when URL changes
  useEffect(() => {
    const countryParam = searchParams.get("country");
    const cityParam = searchParams.get("city");
    
    // Only restore if countries are loaded and we have URL params
    if (countries.length > 0 && countryParam) {
      const countryId = Number(countryParam);
      const countryExists = countries.find((item: any) => item.id === countryId);
      if (countryExists && country !== countryId) {
        setCountry(countryId);
        const region: any = countries.find((item: any) => item.id === countryId);
        if (region && countryData.length > 0) {
          updateAvailableRegions(region.value);
        }
      }
    }
  }, [searchParams, countries, countryData]);

  // Restore city after regions are loaded
  useEffect(() => {
    const cityParam = searchParams.get("city");
    if (cityParam && availableRegions.length > 0 && city !== cityParam) {
      const cityExists = availableRegions.find((r: any) => r.region_code === cityParam);
      if (cityExists) {
        setCity(cityParam);
        // Filtering will be handled by the useEffect that watches country and city changes
      }
    }
  }, [searchParams, availableRegions]);

  // Update available regions when country changes
  useEffect(() => {
    const region: any = countries.find((item: any) => item.id === country);
    if (region && countries.length > 0) {
      updateAvailableRegions(region.value);

      // Auto-select the first city when regions are available and no city is currently selected
      // Only do this if there's no city param in URL (to avoid overriding URL params)
      const cityParam = searchParams.get("city");
      if (availableRegions.length > 0 && !city && !cityParam) {
        const firstRegion = availableRegions[0];
        setCity(firstRegion.region_code);

        // Update URL params
        const newSearchParams = new URLSearchParams(searchParams.toString());
        newSearchParams.set("city", firstRegion.region_code);
        setSearchParams(newSearchParams);
      }
    }
  }, [country, countryData, countries, availableRegions]);

  // Filter data when country or city changes
  useEffect(() => {
    if (countries.length > 0 && pricingDetails.length > 0) {
      const region: any = countries.find((item: any) => item.id === country);
      if (region) {
        if (city) {
          // Filter by both country and city
          const filteredData = pricingDetails.filter(
            (item: any) => item.country_code === region.value && item.region_code === city
          );
          setFilteredPricingDetails(filteredData);
        } else {
          // Filter by country only
          const filteredData = pricingDetails.filter(
            (item: any) => item.country_code === region.value
          );
          setFilteredPricingDetails(filteredData);
        }
      }
    }
  }, [country, city, countries, pricingDetails]);

  return (
    <Box className="mainContainer">
      <Box className="sidePanelContainer">
        <SidePanel isOpen={true} onClose={() => {}} />
      </Box>
      <Box className="dashboardContainer">
        <TopBar />
        <Box style={{ width: "100%", padding: "20px", marginTop: "5%" }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "200px",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "20px",
                }}
              >
                <Typography style={{ fontWeight: 700, fontSize: 20 }}>
                  Helper Pricing Management
                </Typography>
                <Box>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "end",
                      marginBottom: "20px",
                    }}
                  >
                    {/* <Button variant="contained" onClick={navigateToCreate}>
                      Create Helper Pricing
                    </Button>
                    <Button
                      variant="contained"
                      style={{ marginLeft: "5px", backgroundColor: "#FF797D" }}
                    >
                      <DeleteIcon
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          handleDeletePricingModal();
                        }}
                      />
                    </Button> */}
                  </div>
                </Box>

                {/* Delete Pricing Modal */}
                {openDeletePricingModal && (
                  <PricingDeleteModal
                    pricingIds={[
                      filteredPricingDetails[0]._id,
                      filteredPricingDetails.length > 1 && filteredPricingDetails[1]._id,
                    ]}
                    openModal={openDeletePricingModal}
                    setOpenModal={setOpenDeletePricingModal}
                    pricingDetails={pricingDetails}
                    setPricingData={setPricingData}
                    fetchUpdatedData={getData}
                  />
                )}
              </div>
              <Box style={{ marginBottom: "50px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                  <Box>
                    <Typography>Country</Typography>
                    <TextField
                      id="outlined-basic"
                      size="small"
                      select
                      name="country"
                      variant="outlined"
                      style={{ marginBottom: "10px", width: "100%", textTransform: "capitalize" }}
                      value={country}
                      defaultValue=""
                      onChange={onHandleChange}
                    >
                      {countries.map((option: any) => (
                        <MenuItem
                          key={option.id}
                          value={option.id}
                          sx={{ textTransform: "uppercase" }}
                        >
                          {option.value}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>

                  <Box>
                    <Typography>City</Typography>
                    <TextField
                      id="outlined-basic"
                      size="small"
                      select
                      name="city"
                      variant="outlined"
                      style={{ marginBottom: "10px", width: "100%", textTransform: "capitalize" }}
                      value={city}
                      defaultValue=""
                      onChange={onCityChange}
                      disabled={availableRegions.length === 0}
                    >
                      <MenuItem value="">
                        <em>All Cities</em>
                      </MenuItem>
                      {availableRegions.map((region: any) => (
                        <MenuItem
                          key={region.region_code}
                          value={region.region_code}
                          sx={{ textTransform: "capitalize" }}
                        >
                          {region.region_name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </div>
              </Box>

              <Box sx={{ marginBottom: 7 }}>
                <Typography sx={{ marginBottom: "10px", fontWeight: 500, fontSize: 20 }}>
                  Normal User Pricing
                </Typography>
                <TableContainer component={Paper}>
                  <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead style={{ backgroundColor: "rgb(224, 224, 224)" }}>
                      <TableRow>
                        {/* <TableCell align="center" className="cellContainer">
                          Region
                        </TableCell> */}
                        <TableCell align="center" className="cellContainer">
                          Package Size
                        </TableCell>
                        <TableCell align="center" className="cellContainer">
                          Price per KM
                        </TableCell>
                        <TableCell align="center" className="cellContainer">
                          Price per Min
                        </TableCell>
                        <TableCell align="center" className="cellContainer">
                          Base Price
                        </TableCell>

                        <TableCell align="center" className="cellContainer">
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredPricingDetails.length > 0 &&
                        filteredPricingDetails.map((detail: any, index: number) => {
                          return (
                            <>
                              {detail.user_type === "user" &&
                                detail.price_list.map((indiceRow: any, indiceIndex: number) => {
                                  return (
                                    <TableRow
                                      key={indiceIndex + 1}
                                      sx={{
                                        "&:last-child td, &:last-child th": { border: 0 },
                                        cursor: "pointer",
                                      }}
                                    >
                                      {/* <TableCell align="center">{detail.country_code}</TableCell> */}
                                      <TableCell align="center">{indiceRow.package_size}</TableCell>
                                      <TableCell align="center">
                                        {indiceRow.price_per_km} {currency(detail.country_code)}
                                      </TableCell>
                                      <TableCell align="center">
                                        {indiceRow.price_per_min} {currency(detail.country_code)}
                                      </TableCell>

                                      <TableCell align="center">
                                        {indiceRow.base_price} {currency(detail.country_code)}
                                      </TableCell>

                                      <TableCell align="center">
                                        <EditIcon
                                          style={{ cursor: "pointer" }}
                                          onClick={() => {
                                            navigateToEdit(detail._id, indiceRow.package_size);
                                          }}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                            </>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              <Box>
                <Typography sx={{ marginBottom: "10px", fontWeight: 500, fontSize: 20 }}>
                  Pro User Pricing
                </Typography>
                <TableContainer component={Paper}>
                  <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead style={{ backgroundColor: "rgb(224, 224, 224)" }}>
                      <TableRow>
                        {/* <TableCell align="center" className="cellContainer">
                          Region
                        </TableCell> */}
                        <TableCell align="center" className="cellContainer">
                          Package Size
                        </TableCell>
                        <TableCell align="center" className="cellContainer">
                          Price per KM
                        </TableCell>
                        <TableCell align="center" className="cellContainer">
                          Price per Min
                        </TableCell>

                        <TableCell align="center" className="cellContainer">
                          Base Price
                        </TableCell>

                        <TableCell align="center" className="cellContainer">
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredPricingDetails.length > 0 &&
                        filteredPricingDetails.map((detail: any, index: number) => {
                          return (
                            <>
                              {detail.user_type === "pro-user" &&
                                detail.price_list.map((indiceRow: any, indiceIndex: number) => {
                                  return (
                                    <TableRow
                                      key={indiceIndex + 1}
                                      sx={{
                                        "&:last-child td, &:last-child th": { border: 0 },
                                        cursor: "pointer",
                                      }}
                                    >
                                      {/* <TableCell align="center">{detail.country_code}</TableCell> */}
                                      <TableCell align="center">{indiceRow.package_size}</TableCell>
                                      <TableCell align="center">
                                        {indiceRow.price_per_km} {currency(detail.country_code)}
                                      </TableCell>
                                      <TableCell align="center">
                                        {indiceRow.price_per_min} {currency(detail.country_code)}
                                      </TableCell>

                                      <TableCell align="center">
                                        {indiceRow.base_price} {currency(detail.country_code)}
                                      </TableCell>

                                      <TableCell align="center">
                                        <EditIcon
                                          style={{ cursor: "pointer" }}
                                          onClick={() => {
                                            navigateToEdit(detail._id, indiceRow.package_size);
                                          }}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                            </>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default HelperPricing;
